
(() => {
  if (window.__artworkViewerInstalled) return;
  window.__artworkViewerInstalled = true;

  const style = document.createElement('style');
  style.textContent = `
    .artwork-viewer {
      position: fixed;
      inset: 0;
      z-index: 2147483000;
      background: #111;
      color: white;
      display: none;
      overflow: hidden;
    }
    .artwork-viewer.is-open { display: block; }
    .artwork-viewer__stage {
      position: absolute;
      inset: 0;
      overflow: hidden;
      cursor: zoom-in;
      touch-action: none;
    }
    .artwork-viewer__stage.is-zoomed { cursor: grab; }
    .artwork-viewer__stage.is-dragging { cursor: grabbing; }
    .artwork-viewer__image {
      position: absolute;
      left: 50%;
      top: 50%;
      max-width: none;
      max-height: none;
      transform-origin: center center;
      user-select: none;
      pointer-events: none;
      -webkit-user-drag: none;
    }
    .artwork-viewer__controls {
      position: absolute;
      top: 16px;
      right: 16px;
      display: flex;
      gap: 8px;
      z-index: 2;
    }
    .artwork-viewer__controls button {
      background: white;
      color: #111;
      border: 0;
      border-radius: 6px;
      padding: 10px 14px;
      font: inherit;
      cursor: pointer;
      min-height: 42px;
    }
    .artwork-viewer__hint {
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #000a;
      padding: 8px 14px;
      border-radius: 6px;
      font: 13px sans-serif;
      text-align: center;
      pointer-events: none;
    }
    body.artwork-viewer-open {
      overflow: hidden !important;
    }
  `;
  document.head.appendChild(style);

  const viewer = document.createElement('div');
  viewer.className = 'artwork-viewer';
  viewer.setAttribute('role', 'dialog');
  viewer.setAttribute('aria-modal', 'true');
  viewer.setAttribute('aria-label', 'Artwork viewer');

  viewer.innerHTML = `
    <div class="artwork-viewer__stage">
      <img class="artwork-viewer__image" alt="">
    </div>
    <div class="artwork-viewer__controls">
      <button type="button" data-action="out">−</button>
      <button type="button" data-action="in">+</button>
      <button type="button" data-action="reset">Reset</button>
      <button type="button" data-action="close">×</button>
    </div>
    <div class="artwork-viewer__hint">
      Scroll to zoom · Drag to explore · Esc to close
    </div>
  `;

  document.body.appendChild(viewer);

  const stage = viewer.querySelector('.artwork-viewer__stage');
  const image = viewer.querySelector('.artwork-viewer__image');

  let scale = 1;
  let x = 0;
  let y = 0;
  let fit = 1;
  let dragging = false;
  let moved = false;
  let pointerX = 0;
  let pointerY = 0;
  let activePointer = null;
  let previousFocus = null;

  const clamp = (v, a, b) =>
    Math.max(a, Math.min(b, v));

  function draw() {
    const w = image.naturalWidth || 1;
    const h = image.naturalHeight || 1;

    const displayedW = w * fit * scale;
    const displayedH = h * fit * scale;

    const maxX = Math.max(
      0, (displayedW - stage.clientWidth) / 2
    );
    const maxY = Math.max(
      0, (displayedH - stage.clientHeight) / 2
    );

    x = clamp(x, -maxX, maxX);
    y = clamp(y, -maxY, maxY);

    image.style.width = `${w}px`;
    image.style.height = `${h}px`;

    image.style.transform =
      `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) ` +
      `scale(${fit * scale})`;

    stage.classList.toggle('is-zoomed', scale > 1);
  }

  function reset() {
    scale = 1;
    x = 0;
    y = 0;
    draw();
  }

  function zoom(
    factor,
    cx = stage.clientWidth / 2,
    cy = stage.clientHeight / 2
  ) {
    const old = scale;
    scale = clamp(scale * factor, 1, 20);

    const ratio = scale / old;

    x = (x - (cx - stage.clientWidth / 2)) * ratio
      + (cx - stage.clientWidth / 2);

    y = (y - (cy - stage.clientHeight / 2)) * ratio
      + (cy - stage.clientHeight / 2);

    draw();
  }

  function resize() {
    if (!image.naturalWidth || !image.naturalHeight) return;

    fit = Math.min(
      stage.clientWidth / image.naturalWidth,
      stage.clientHeight / image.naturalHeight,
      1
    );

    draw();
  }

  function open(source) {
    previousFocus = document.activeElement;

    const src =
      source.dataset.maxResolution ||
      source.getAttribute('data_max_resolution') ||
      source.currentSrc ||
      source.src;

    if (!src) return;

    image.alt = source.alt || 'Artwork';

    viewer.classList.add('is-open');
    document.body.classList.add('artwork-viewer-open');

    image.onload = () => {
      resize();
      reset();
    };

    image.src = src;

    if (image.complete && image.naturalWidth) {
      resize();
      reset();
    }

    viewer.querySelector('[data-action="close"]').focus();
  }

  function close() {
    viewer.classList.remove('is-open');
    document.body.classList.remove('artwork-viewer-open');
    image.removeAttribute('src');

    if (previousFocus?.focus) {
      previousFocus.focus();
    }
  }

  viewer.querySelectorAll('[data-action]').forEach(button => {
    button.addEventListener('click', () => {
      switch (button.dataset.action) {
        case 'close':
          close();
          break;
        case 'reset':
          reset();
          break;
        case 'in':
          zoom(1.5);
          break;
        case 'out':
          zoom(1 / 1.5);
          break;
      }
    });
  });

  stage.addEventListener('wheel', event => {
    event.preventDefault();

    const rect = stage.getBoundingClientRect();

    zoom(
      Math.exp(-event.deltaY * 0.002),
      event.clientX - rect.left,
      event.clientY - rect.top
    );
  }, { passive: false });

  stage.addEventListener('pointerdown', event => {
    if (event.button !== 0 || scale <= 1) return;

    dragging = true;
    moved = false;
    activePointer = event.pointerId;

    pointerX = event.clientX;
    pointerY = event.clientY;

    stage.setPointerCapture(event.pointerId);
    stage.classList.add('is-dragging');
  });

  stage.addEventListener('pointermove', event => {
    if (!dragging || event.pointerId !== activePointer) {
      return;
    }

    const dx = event.clientX - pointerX;
    const dy = event.clientY - pointerY;

    if (Math.abs(dx) + Math.abs(dy) > 2) {
      moved = true;
    }

    x += dx;
    y += dy;

    pointerX = event.clientX;
    pointerY = event.clientY;

    draw();
  });

  function endDrag(event) {
    if (event.pointerId !== activePointer) return;

    dragging = false;
    activePointer = null;
    stage.classList.remove('is-dragging');
  }

  stage.addEventListener('pointerup', endDrag);
  stage.addEventListener('pointercancel', endDrag);

  stage.addEventListener('click', event => {
    if (!moved) {
      const rect = stage.getBoundingClientRect();

      zoom(
        scale === 1 ? 2 : 1 / scale,
        event.clientX - rect.left,
        event.clientY - rect.top
      );
    }

    moved = false;
  });

  document.addEventListener('keydown', event => {
    if (
      viewer.classList.contains('is-open') &&
      event.key === 'Escape'
    ) {
      event.preventDefault();
      close();
    }
  });

  window.addEventListener('resize', resize);

  // Open artwork when clicking a product gallery image.
  document.addEventListener('click', event => {
    if (viewer.classList.contains('is-open')) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const img = target.closest('img');
    if (!img) return;

    const media = img.closest(
      'media-gallery, .product-media-container, ' +
      '.product-media, [data-product-media]'
    );

    if (!media || img.closest('a[href]')) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    open(img);
  }, true);
})();
